// Lib
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash/fp';
import { partition } from 'lodash';

// Utils
import { selectCurrentlyAssignedUserId, selectFilteredSuggestions } from './taskAssignmentPopupSelector';

// Actions
import { assignTask, removeTaskAssignments } from './taskAssignmentActions';
import { startEditingElement } from '../../../selection/selectionActions';
import { closePopup } from '../../../../components/popupPanel/popupActions';

// Components
import AssignmentSearchForm from './AssignmentSearchForm';
import AssignmentSuggestionResults from './AssignmentSuggestionResults';

// Hooks
import useTaskPopupFocus from './useTaskPopupFocus';
import useListKeyboardManager, { ListDirection } from '../../../../components/lists/useListKeyboardManager';
import useListFocusManager from '../../../../components/lists/useListFocusManager';

// Styles
import './TaskAssignmentPopupContent.scss';

const TaskAssignmentPopupContent = (props) => {
    const { popupId, elementId } = props;

    const [searchTerm, setSearchTerm] = useState('');

    const suggestions = useSelector((state) => selectFilteredSuggestions(state, { searchTerm }));
    const [enabledSuggestions, disabledSuggestions] = partition(suggestions, (suggestion) => !suggestion.disabled);
    const currentlyAssignedUserId = useSelector((state) => selectCurrentlyAssignedUserId(state, { elementId }));

    const dispatch = useDispatch();

    const dispatchAssignTask = ({ elementId, popupId, suggestion }) => {
        if (!suggestion) return;

        dispatch(
            assignTask({
                taskId: elementId,
                assigneeId: suggestion.id,
                assigneeName: suggestion.name,
            }),
        );

        dispatch(closePopup(popupId, false));
    };

    const dispatchRemoveTaskAssignments = ({ elementId, popupId }) => {
        dispatch(removeTaskAssignments({ id: elementId }));
        dispatch(closePopup(popupId));
    };

    const dispatchSetFocusToPopupInput = ({ elementId }) =>
        dispatch(
            startEditingElement({
                id: elementId,
                editorId: `${elementId}-assignment`,
                editorKey: 'TASK',
            }),
        );

    const onClickSuggestion = (suggestion) => dispatchAssignTask({ elementId, popupId, suggestion });

    const { focusedIndex, moveListFocusDown, moveListFocusUp } = useListFocusManager({
        initialFocusIndex: -1,
        listSize: enabledSuggestions ? enabledSuggestions.length : 0,
    });

    useListKeyboardManager({
        listDirection: ListDirection.VERTICAL,
        moveListFocusDown,
        moveListFocusUp,
        onClickSuggestion,
        onSubmit: () => {
            const suggestion = enabledSuggestions[focusedIndex];
            if (!suggestion) return;

            onClickSuggestion(suggestion);
        },
        onClose: () => dispatch(closePopup(popupId)),
        onDelete: (event) => {
            // If the search term isn't empty, let the browser handle the backspace / delete
            if (!isEmpty(searchTerm)) return;

            event.preventDefault();
            event.stopPropagation();

            return dispatchRemoveTaskAssignments({ elementId, popupId });
        },
    });

    // This is used so the original task editor can have its focus returned to
    useTaskPopupFocus({
        elementId,
        closePopup: () => dispatch(closePopup(popupId)),
        dispatchSetFocusToPopupInput,
    });

    return (
        <div className="TaskAssignmentPopupContent">
            <div className="search-section">
                <AssignmentSearchForm searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            </div>
            <AssignmentSuggestionResults
                currentlyAssignedUserId={currentlyAssignedUserId}
                searchTerm={searchTerm}
                focusedIndex={focusedIndex}
                enabledSuggestions={enabledSuggestions}
                disabledSuggestions={disabledSuggestions}
                onClickSuggestion={onClickSuggestion}
            />
        </div>
    );
};

TaskAssignmentPopupContent.propTypes = {
    popupId: PropTypes.string,
    elementId: PropTypes.string,
};

export default TaskAssignmentPopupContent;
